import { useRequiredContext } from "@redotech/react-util/context";
import { useMutation, type UseMutationOptions } from "@tanstack/react-query";
import type { Context } from "react";

/**
 * A dynamic hook to call any mutation method from any RPC client.
 * Accepts a client context as the first argument and retrieves the client from it.
 *
 * @param clientContext - The React context containing the RPC client
 * @param options - Configuration object containing:
 *   - method: The method name to call on the RPC client
 *   - options: Optional react-query mutation options
 *
 * @example
 * ```
 * import { RedoMarketingRpcClientContext } from "@redotech/redo-merchant-app-common/marketing-rpc-client-provider";
 *
 * // In your component
 * const mutation = useRpcMutation(RedoMarketingRpcClientContext, {
 *   method: 'createCampaign',
 *   options: {
 *     onSuccess: (data) => {
 *       // Handle success
 *       queryClient.invalidateQueries(['marketing', 'getTeamCampaigns']);
 *     },
 *     onError: (error) => {
 *       // Handle error
 *       console.error('Failed to create campaign:', error);
 *     }
 *   }
 * });
 *
 * // Then you can call the mutation with your data
 * mutation.mutate({
 *   name: 'New Campaign',
 *   description: 'This is a new campaign',
 *   // other campaign properties
 * });
 * ```
 */
export function useRpcMutation<
  TClient,
  M extends keyof TClient,
  TArgs extends TClient[M] extends Callable ? Parameters<TClient[M]>[0] : never,
  TData extends TClient[M] extends Callable
    ? Awaited<ReturnType<TClient[M]>>
    : never,
  TError extends Error = Error,
  TContext = unknown,
>(
  clientContext: Context<TClient | undefined>,
  {
    method,
    options,
  }: {
    method: M;
    options?: Omit<
      UseMutationOptions<TData, TError, TArgs, TContext>,
      "mutationFn"
    >;
  },
) {
  // Get the client instance from context
  const clientInstance = useRequiredContext(clientContext);

  return useMutation({
    mutationFn: async (args: TArgs) => {
      // Call the RPC method with the provided variables
      const result = await (clientInstance as any)[method](args);
      return result as TData;
    },
    ...options,
  });
}

type Callable = (...args: any[]) => any;
